import type { Credentials, UserPrivateProfile, UserProfile } from '#shared/types/User'
import type { ValidationResults } from 'last-validator'

import { CompletenessWeight, useCompleteness } from '@/composables/useCompleteness'
import { isRichtextRequired } from '@/validators/isRichtextRequired'
import { isEmail, isRequired, validate } from 'last-validator'

export function validateUserProfile(userProfile: UserProfile) {
    return validate<UserProfile>(userProfile, {
        firstName: [isRequired()],
        lastName: [isRequired()],
        hobby: [isRichtextRequired()],
        interests: [isRequired()]
    })
}

export function validateCredentials(credentials: Credentials) {
    return validate<Credentials>(credentials, {
        email: [isRequired(), isEmail()],
        password: [isRequired()]
    })
}

export function checkUserProfileComplete(userProfile: UserProfile) {
    const { evaluate } = useCompleteness()
    
    return evaluate(userProfile, {
        firstName: {
            mandatory: true,
            minLength: 1,
            weight: CompletenessWeight.MAX
        },
        lastName: {
            mandatory: true,
            minLength: 1,
            weight: CompletenessWeight.MAX
        },
        geoCountry: {
            minLength: 2,
            weight: CompletenessWeight.LOW
        },
        geoRegion: {
            weight: CompletenessWeight.LOW
        },
        geoCity: {
            weight: CompletenessWeight.LOW
        },
        shortProfile: {
            minLength: 5,
            weight: CompletenessWeight.LOW
        },
        hobby: {
            mandatory: true,
            minLength: 5,
            weight: CompletenessWeight.MAX
        },
        interests: {
            mandatory: true,
            weight: CompletenessWeight.LOW
        },
        highSchools: {
            weight: CompletenessWeight.LOW
        },
        universities: {
            weight: CompletenessWeight.LOW
        },
        jobs: {
            weight: CompletenessWeight.LOW
        },
        achievements: {
            weight: CompletenessWeight.LOW
        },
        responseTime: {
            weight: CompletenessWeight.MID
        },
        responseUnit: {
            weight: CompletenessWeight.MID
        }
    })
}

export async function validateProfileData(data: {
    userPrivateProfile: UserPrivateProfile
    userProfile: UserProfile
    under18: boolean
    consents: { hasAgreedToRules?: boolean }
    livingAbroad?: boolean
}): Promise<ValidationResults> {
    const privateProfileResults = await validate<UserPrivateProfile>(data.userPrivateProfile, {
        birthday: data.under18 ? [isRequired()] : []
    })

    const profileResults = await validate<UserProfile>(data.userProfile, {
        firstName: [isRequired()],
        lastName: [isRequired()],
        geoCountry: data.livingAbroad ? [isRequired()] : []
    })

    const consentResults = await validate<{
        hasAgreedToRules: boolean
    }>(data.consents, {
        hasAgreedToRules: [isRequired('Vyžaduje sa súhlas.')]
    })

    return {
        isValid: privateProfileResults.isValid && profileResults.isValid && consentResults.isValid,
        errors: {
            ...privateProfileResults.errors,
            ...profileResults.errors,
            ...consentResults.errors
        }
    }
}
